import { Preferences } from '@capacitor/preferences';
import { Filesystem, Directory } from '@capacitor/filesystem';

const BOOKS_CACHE_KEY = 'books_cache';
const DOWNLOADED_IDS_KEY = 'downloaded_ids';
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'user_data';

// ---------- Preferences (key-value) ----------
export const setItem = async (key, value) => {
  await Preferences.set({ key, value: JSON.stringify(value) });
};

export const getItem = async (key) => {
  const { value } = await Preferences.get({ key });
  return value ? JSON.parse(value) : null;
};

export const removeItem = async (key) => {
  await Preferences.remove({ key });
};

// ---------- Books cache ----------
export const cacheBooks = async (books) => {
  await setItem(BOOKS_CACHE_KEY, books);
};

export const getCachedBooks = async () => {
  return await getItem(BOOKS_CACHE_KEY);
};

// ---------- Downloaded book IDs ----------
export const getDownloadedIds = async () => {
  const ids = await getItem(DOWNLOADED_IDS_KEY);
  return ids || [];
};

export const addDownloadedId = async (bookId) => {
  const current = await getDownloadedIds();
  if (!current.includes(bookId)) {
    current.push(bookId);
    await setItem(DOWNLOADED_IDS_KEY, current);
  }
};

export const removeDownloadedId = async (bookId) => {
  const current = await getDownloadedIds();
  const updated = current.filter(id => id !== bookId);
  await setItem(DOWNLOADED_IDS_KEY, updated);
};

// ---------- Auth ----------
export const saveToken = async (token) => {
  await setItem(TOKEN_KEY, token);
};

export const getToken = async () => {
  return await getItem(TOKEN_KEY);
};

export const saveUser = async (user) => {
  await setItem(USER_KEY, user);
};

export const getUser = async () => {
  return await getItem(USER_KEY);
};

export const clearAuth = async () => {
  await removeItem(TOKEN_KEY);
  await removeItem(USER_KEY);
};

// ---------- Filesystem (PDF) ----------
const BOOKS_DIR = 'books';

export const getBookFilePath = (bookId) => {
  return `${BOOKS_DIR}/${bookId}.pdf`;
};

export const saveBookFile = async (bookId, data) => {
  // data is a Blob or ArrayBuffer
  const path = getBookFilePath(bookId);
  // Convert Blob to base64 or arraybuffer
  let base64Data;
  if (data instanceof Blob) {
    const arrayBuffer = await data.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    base64Data = btoa(binary);
  } else {
    // assume it's already base64 or arraybuffer
    base64Data = data;
  }
  await Filesystem.writeFile({
    path,
    data: base64Data,
    directory: Directory.External, // private app directory
    recursive: true,
  });
  // after saving, add to downloaded IDs
  await addDownloadedId(bookId);
};

export const getBookFile = async (bookId) => {
  const path = getBookFilePath(bookId);
  try {
    const result = await Filesystem.readFile({
      path,
      directory: Directory.External,
    });
    // result.data is base64 string
    return result.data;
  } catch (e) {
    return null;
  }
};

export const deleteBookFile = async (bookId) => {
  const path = getBookFilePath(bookId);
  try {
    await Filesystem.deleteFile({
      path,
      directory: Directory.External,
    });
    await removeDownloadedId(bookId);
  } catch (e) {
    // file might not exist
  }
};

export const isBookDownloaded = async (bookId) => {
  const ids = await getDownloadedIds();
  return ids.includes(bookId);
};